import * as React from 'react'
import {
  ModalStackEntry,
  useModalStackManager,
} from '../contexts/ModalStackContext'
import { useObservableRef } from './mobx-hooks'
import { FrameValue, useSpring } from 'react-spring'
import { expr } from 'mobx-utils'

export interface ModalState {
  close?: () => unknown
  readonly showing: boolean
}

export interface UseModalStackProps {
  /**
   * If `true`, disables closing on Esc click.
   */
  disableCloseOnEsc?: boolean
}

/**
 * Adds an entry to the modal stack when the modal is showing and allows
 * closing it with Esc.
 *
 * @param modal
 * @param props
 */
export function useModalStack(
  modal: ModalState,
  props: UseModalStackProps = {},
) {
  const stackManager = useModalStackManager()
  const entryRef = useObservableRef<ModalStackEntry | null>(null)
  React.useEffect(() => {
    const entry = entryRef.current || stackManager.add()
    entryRef.set(entry)
    entry.update(modal.showing, modal.close, !props.disableCloseOnEsc)
    return () => {
      if (entryRef.current && !modal.showing) {
        entryRef.current.remove()
        entryRef.set(null)
      }
    }
  }, [modal.showing, modal.close, props.disableCloseOnEsc])

  React.useEffect(() => {
    // Cleanup in case a Chungus is pulled on the Dialog props.
    return () => {
      entryRef.current?.remove()
      entryRef.set(null)
    }
  }, [])

  const layer = expr(() => entryRef.current?.layer || 0)
  const layerSpring = useSpring<{ layer: typeof layer }>({ layer }).layer
  const isFirstResponder = layer === 1
  const stackDepthStyle = getStackDepthStyleSprings(layerSpring)
  return {
    layer,
    layerSpring,
    isFirstResponder,
    stackDepthStyle,
  }
}

/**
 * Gets the styles to apply to an animated element to push it back into z-space.
 *
 * @param layer
 */
function getStackDepthStyleSprings(layer: FrameValue<number>) {
  layer = layer.to((v) => Math.max(0, v - 1)) // First layer should not have effects applied
  return {
    transformOrigin: 'top center',
    transform: layer.to(
      (v) => `translateY(-${v * 15}px) scale(${1 - v * 0.03})`,
    ),
  }
}
